import { useMemo, type Key } from 'react';
import { Space, Tag, Typography } from 'antd';
import type { ColumnsType, TableProps } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import type { IncidentStatus } from '@evimo/types';
import type { IncidentListItem } from '@/app/services/incident/incident.service';
import { incidentStatusConfig } from '@/app/dashboard/mock-data';
import { useTimeAgo } from '@/app/hooks/useTimeAgo';
import { IncidentOmpExpandedRow } from '@/app/components/incident-overview/IncidentOmpExpandedRow';
import { IncidentTableRowControls } from '@/app/components/incident-overview/IncidentTableRowControls';
import { OMP_CRITICALITY_COLORS } from '@/app/components/incident-overview/incident-omp.constants';
import { incidentTableColumnProps } from '@/app/components/incident-overview/utils/incident-table-column.utils';
import { IncidentStationAssetCell } from '@/app/components/incident/IncidentStationAssetCell/IncidentStationAssetCell';
import { formatEuro } from '~/app/landing/sla/shared/utils/sla-format.utils';
import { incidentDisplayCode } from '~/app/utils/incident-display-code';
import { formatIncidentOrigins } from '~/app/utils/incident-origins';

const { Text, Paragraph } = Typography;

function renderPill(label: string, color: string) {
  return (
    <Tag
      bordered={false}
      style={{
        marginInlineEnd: 0,
        borderRadius: 9999,
        paddingInline: '0.625rem',
        paddingBlock: '0.25rem',
        fontWeight: 600,
        fontSize: '0.75rem',
        lineHeight: 1.2,
        color: '#191c1f',
        background: `${color}1A`,
        border: `1px solid ${color}33`,
      }}
    >
      {label}
    </Tag>
  );
}

function formatOpenDuration(openHours: number): string {
  if (openHours < 24) {
    return `${Math.round(openHours)}h`;
  }

  const days = Math.floor(openHours / 24);
  const hours = Math.round(openHours % 24);

  if (hours === 0) {
    return `${days}d`;
  }

  return `${days}d ${hours}h`;
}

export function useIncidentTableColumns({
  expandedRowKeys,
  onExpandedRowsChangeAction,
  onOpenDetailsAction,
}: {
  expandedRowKeys: readonly string[];
  onExpandedRowsChangeAction: (keys: readonly Key[]) => void;
  onOpenDetailsAction: (incidentId: string) => void;
}) {
  const { t } = useTranslation('incidents');
  const { timeAgo } = useTimeAgo();

  const expandable = useMemo<TableProps<IncidentListItem>['expandable']>(
    () => ({
      columnWidth: 112,
      expandedRowKeys,
      onExpandedRowsChange: onExpandedRowsChangeAction,
      expandedRowClassName: (record) =>
        record.omp ? 'incident-group-end' : '',
      expandedRowRender: (record) =>
        record.omp ? <IncidentOmpExpandedRow incident={record} /> : null,
      rowExpandable: () => true,
      expandIcon: ({ expanded, onExpand, record }) => (
        <IncidentTableRowControls
          record={record}
          expanded={expanded}
          onExpand={onExpand}
          onOpenDetailsAction={onOpenDetailsAction}
        />
      ),
    }),
    [expandedRowKeys, onExpandedRowsChangeAction, onOpenDetailsAction],
  );

  const columns: ColumnsType<IncidentListItem> = useMemo(
    () => [
      {
        title: t('overview.table.impactBand', 'Impact'),
        key: 'impactBand',
        width: 88,
        ...incidentTableColumnProps('impact'),
        render: (_, record) => {
          if (!record.omp) {
            return <Text type="secondary">—</Text>;
          }

          const color =
            OMP_CRITICALITY_COLORS[record.omp.criticalityBand] ?? '#94a3b8';

          return renderPill(
            t(
              `omp.band.${record.omp.criticalityBand}`,
              record.omp.criticalityBand,
            ),
            color,
          );
        },
      },
      {
        title: t('overview.table.incident', 'Incident'),
        dataIndex: 'code',
        key: 'code',
        width: 150,
        ...incidentTableColumnProps('incident'),
        render: (_code: string, record) => (
          <Space
            direction="vertical"
            size={0}
            style={{ width: '100%', minWidth: 0 }}
          >
            <Text
              strong
              ellipsis={{ tooltip: incidentDisplayCode(record) }}
              style={{
                fontFamily: 'monospace',
                fontSize: '0.75rem',
                color: '#191c1f',
              }}
            >
              {incidentDisplayCode(record)}
            </Text>
            <Text
              strong
              ellipsis={{ tooltip: record.title || undefined }}
              style={{
                fontSize: '0.8125rem',
                fontWeight: 700,
                color: '#191c1f',
              }}
            >
              {record.title?.trim() ? record.title : '—'}
            </Text>
            <Text
              type="secondary"
              style={{
                fontSize: '0.6875rem',
                color: 'var(--evimo-text-muted)',
              }}
            >
              {timeAgo(record.createdAt)}
            </Text>
          </Space>
        ),
      },
      {
        title: t('overview.table.station', 'Station / location'),
        key: 'station',
        width: 220,
        ellipsis: true,
        ...incidentTableColumnProps('station'),
        render: (_, record) => (
          <IncidentStationAssetCell
            stationName={record.stationName}
            location={record.location}
            primaryAsset={record.primaryAsset}
          />
        ),
      },
      {
        title: t('overview.table.origin', 'Origin'),
        dataIndex: 'origin',
        key: 'origin',
        width: 120,
        ellipsis: true,
        ...incidentTableColumnProps('origin'),
        render: (origin: string[] | undefined) => {
          const label = formatIncidentOrigins(origin);
          return (
            <Text ellipsis={{ tooltip: label || undefined }}>
              {label || '—'}
            </Text>
          );
        },
      },
      {
        title: t('overview.table.summary', 'Summary'),
        dataIndex: 'aiSummary',
        key: 'aiSummary',
        ellipsis: true,
        ...incidentTableColumnProps('summary'),
        render: (text: string | null) => (
          <Paragraph
            ellipsis={{ rows: 3, tooltip: text ?? undefined }}
            style={{
              margin: 0,
              fontSize: '0.875rem',
              lineHeight: 1.45,
              maxWidth: '100%',
            }}
          >
            {text ?? '—'}
          </Paragraph>
        ),
      },
      {
        title: t('overview.table.totalImpact', 'Total impact'),
        key: 'totalImpact',
        width: 100,
        ...incidentTableColumnProps('impact-total'),
        render: (_, record) =>
          record.omp ? (
            <Text strong>{formatEuro(record.omp.totalImpact)}</Text>
          ) : (
            <Text type="secondary">—</Text>
          ),
      },
      {
        title: t('overview.table.openDuration', 'Open'),
        key: 'openDuration',
        width: 64,
        ...incidentTableColumnProps('open'),
        render: (_, record) =>
          record.omp ? (
            <Text>{formatOpenDuration(record.omp.openHours)}</Text>
          ) : (
            <Text type="secondary">—</Text>
          ),
      },
      {
        title: t('overview.table.status', 'Status'),
        dataIndex: 'status',
        key: 'status',
        width: 180,
        ...incidentTableColumnProps('status'),
        align: 'center',
        onCell: () => ({
          style: { minWidth: 160, maxWidth: 200, textAlign: 'center' },
        }),
        render: (status: IncidentStatus) => {
          const config = incidentStatusConfig[status];
          const label = t(`status.${status}`, status);

          return (
            <Tag
              bordered={false}
              title={label}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                marginInlineEnd: 0,
                maxWidth: '100%',
                height: 'auto',
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                borderRadius: 9999,
                paddingInline: '0.5rem',
                paddingBlock: '0.25rem',
                fontWeight: 600,
                fontSize: '0.6875rem',
                lineHeight: 1.25,
                color: '#191c1f',
                background: `${config.color}1A`,
                border: `1px solid ${config.color}33`,
              }}
            >
              {label}
            </Tag>
          );
        },
      },
    ],
    [t, timeAgo],
  );

  return { columns, expandable };
}

export function getIncidentOmpRowClassName(record: IncidentListItem): string {
  if (!record.omp?.criticalityBand) {
    return '';
  }

  return `incident-omp-row incident-omp-row--${record.omp.criticalityBand}`;
}
